import React, { useState, useEffect, useCallback } from 'react';
import { X, MapPin, Download, Trash2, Loader2, FootprintsIcon, TrendingUp, Clock, Navigation, ChevronDown, ChevronUp, Route, Check, Share2, Tent, Landmark, Star, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { SavedTrack, ElevationPoint, AppUser } from '../types';
import { getUserTracks, deleteTrack, exportTrackAsGpxDownload, formatDistance, formatDuration, formatDate, parseGpxToElevationProfile, convertTrackToRoute, updateTrackPlanStatus } from '../services/trackService';
import TrackShareDialog from './tracks/TrackShareDialog';

import { BRAND } from '../constants/mapColors';
interface MyTracksModalProps {
  userId: string;
  user: AppUser;
  onClose: () => void;
  onViewTrack: (track: SavedTrack) => void;
}

const TrackElevationChart: React.FC<{ gpxUrl: string }> = ({ gpxUrl }) => {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<ElevationPoint[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    parseGpxToElevationProfile(gpxUrl).then(p => {
      if (!cancelled) { setProfile(p); setLoading(false); }
    });
    return () => { cancelled = true; };
  }, [gpxUrl]);

  if (loading) return <div className="flex justify-center py-3"><Loader2 size={16} className="animate-spin text-[var(--text-secondary)]" /></div>;
  if (!profile || profile.length < 2) return <p className="text-2xs text-[var(--text-secondary)] text-center py-2">{t("auto.mytracksmodal.ingen_hoydedata_i_gpx", "Ingen høydedata i GPX")}</p>;

  const W = 300; const H = 56;
  const maxDist = profile[profile.length - 1].distance;
  const eles = profile.map(p => p.elevation);
  const minE = Math.min(...eles); const maxE = Math.max(...eles);
  const range = maxE - minE || 1;
  const gain = eles.reduce((g, e, i) => i > 0 && e > eles[i - 1] ? g + (e - eles[i - 1]) : g, 0);
  const pts = profile.map(p => {
    const x = (p.distance / maxDist) * W;
    const y = H - ((p.elevation - minE) / range) * (H - 10) - 5;
    return `${x},${y}`;
  }).join(' ');
  const fillD = `M ${pts} L ${W},${H} L 0,${H} Z`;

  return (
    <div className="bg-stone-50 rounded-2xl p-3 mt-2 border border-stone-100">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-3xs font-black text-[var(--text-secondary)] uppercase tracking-widest">{t("auto.mytracksmodal.hoydeprofil", "Høydeprofil")}</span>
        <div className="flex items-center gap-2 text-3xs font-bold text-[var(--text-muted)]">
          <span>Min: {Math.round(minE)}m</span>
          <span>Max: {Math.round(maxE)}m</span>
          <span className="text-emerald-600">+{Math.round(gain)}m</span>
        </div>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14 overflow-visible" preserveAspectRatio="none">
        <defs>
          <linearGradient id="trackEleGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={BRAND} stopOpacity="0.3" />
            <stop offset="100%" stopColor={BRAND} stopOpacity="0.03" />
          </linearGradient>
        </defs>
        <path d={fillD} fill="url(#trackEleGrad)" />
        <polyline points={pts} fill="none" stroke={BRAND} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      </svg>
    </div>
  );
};

const MyTracksModal: React.FC<MyTracksModalProps> = ({ userId, user, onClose, onViewTrack }) => {
  const { t } = useTranslation();
  const [tracks, setTracks] = useState<SavedTrack[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedProfileId, setExpandedProfileId] = useState<string | null>(null);

  // Konvertering tur → rute
  const [convertingId, setConvertingId] = useState<string | null>(null);
  const [sharingTrack, setSharingTrack] = useState<SavedTrack | null>(null);
  const [tabFilter, setTabFilter] = useState<'all' | 'recorded' | 'planned'>('all');

  const loadTracks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getUserTracks(userId);
      setTracks(result);
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (msg.includes('ERR_BLOCKED_BY_CLIENT') || msg.toLowerCase().includes('blocked by client')) {
        setError('Kunne ikke laste turer fordi nettleseren blokkerte forespørselen. Deaktiver adblock/privacy-filter for fricamp.');
      } else {
        setError('Kunne ikke laste turer. Sjekk internettforbindelsen.');
      }
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadTracks();
  }, [loadTracks]);

  const handleDelete = async (track: SavedTrack) => {
    setDeletingId(track.id);
    try {
      await deleteTrack(track.id, track.gpxUrl);
      setTracks(prev => prev.filter(t => t.id !== track.id));
    } catch {
      setError('Kunne ikke slette turen. Prøv igjen.');
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleConvertToRoute = async (track: SavedTrack) => {
    if (!user) return;
    setConvertingId(track.id);
    try {
      const routeId = await convertTrackToRoute(track, user);
      if (routeId) {
        setTracks(prev => prev.map(t => t.id === track.id ? { ...t, linkedRouteId: routeId } : t));
      }
    } catch {
      setError(t('tracks.convert_error'));
    } finally {
      setConvertingId(null);
    }
  };

  const totalDistance = tracks.reduce((sum, t) => sum + t.distance, 0);
  const totalDuration = tracks.reduce((sum, t) => sum + t.duration, 0);

  return (
    <div className="fixed inset-0 z-drawer flex items-end md:items-start md:justify-end pointer-events-none">
      <div
        className="pointer-events-auto w-full md:w-80 max-h-[85dvh] md:max-h-[calc(100vh-120px)] md:mt-4 md:mr-4
          bg-white/95 backdrop-blur-xl border border-stone-200 rounded-t-3xl md:rounded-3xl
          shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-2 md:slide-in-from-right-2"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-stone-100 flex-shrink-0">
          <div className="flex items-center gap-2">
            <FootprintsIcon size={16} className="text-emerald-600" />
            <span className="text-sm font-bold text-stone-800">{t("auto.mytracksmodal.mine_turer", "Mine turer")}</span>
          </div>
          <button
            onClick={onClose}
            className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full hover:bg-stone-100 text-[var(--text-muted)]"
          >
            <X size={15} />
          </button>
        </div>

        {/* Statistikk-sammendrag */}
        {tracks.length > 0 && (
          <div className="flex items-center justify-around px-5 py-3 bg-stone-50 border-b border-stone-100 flex-shrink-0">
            <div className="text-center">
              <div className="text-2xs text-[var(--text-secondary)] mb-0.5">{t("auto.mytracksmodal.turer", "Turer")}</div>
              <div className="text-sm font-black text-stone-800">{tracks.length}</div>
            </div>
            <div className="text-center">
              <div className="text-2xs text-[var(--text-secondary)] mb-0.5">{t("auto.mytracksmodal.total_distanse", "Total distanse")}</div>
              <div className="text-sm font-black text-stone-800">{formatDistance(totalDistance)}</div>
            </div>
            <div className="text-center">
              <div className="text-2xs text-[var(--text-secondary)] mb-0.5">{t("auto.mytracksmodal.total_tid", "Total tid")}</div>
              <div className="text-sm font-black text-stone-800">{formatDuration(totalDuration)}</div>
            </div>
          </div>
        )}

        {/* Tab-filtrering */}
        {tracks.length > 0 && (
          <div className="flex items-center gap-1 px-5 py-2 border-b border-stone-100 flex-shrink-0">
            {(['all', 'recorded', 'planned'] as const).map(tab => {
              const count = tab === 'all' ? tracks.length
                : tab === 'recorded' ? tracks.filter(t => !t.trackType || t.trackType === 'recorded').length
                : tracks.filter(t => t.trackType === 'planned').length;
              const label = tab === 'all' ? t('routePlanner.tab_all', 'Alle')
                : tab === 'recorded' ? t('routePlanner.tab_recorded', 'Registrerte')
                : t('routePlanner.tab_planned', 'Planlagte');
              return (
                <button key={tab} onClick={() => setTabFilter(tab)}
                  className={`px-3 py-1.5 rounded-full text-2xs font-bold transition-colors ${
                    tabFilter === tab
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'text-stone-400 hover:text-stone-600 hover:bg-stone-50'
                  }`}>
                  {label} {count > 0 && <span className="ml-1 text-3xs">({count})</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Innhold */}
        <div className="overflow-y-auto flex-1 custom-scrollbar px-5 py-4" style={{ overscrollBehavior: 'contain' }}>
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={24} className="animate-spin text-emerald-500" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-[12px] text-red-600 mb-3">
              {error}
            </div>
          )}

          {!loading && tracks.length === 0 && (
            <div className="text-center py-10">
              <FootprintsIcon size={36} className="text-[var(--text-primary)] mx-auto mb-3" />
              <p className="text-sm font-bold text-[var(--text-secondary)]">{t("auto.mytracksmodal.ingen_turer_registrert_enna", "Ingen turer registrert ennå")}</p>
              <p className="text-tiny text-[var(--text-secondary)] mt-1 leading-relaxed">
                {t("auto.mytracksmodal.start_en_tur_via_kartet_for_a_", "Start en tur via kartet for å logge GPS-spor, distanse og høydemeter")}
              </p>
            </div>
          )}

          {!loading && tracks.length > 0 && (
            <div className="space-y-3">
              {tracks
                .filter(track => {
                  if (tabFilter === 'all') return true;
                  if (tabFilter === 'planned') return track.trackType === 'planned';
                  return !track.trackType || track.trackType === 'recorded';
                })
                .map(track => {
                  // Planlagt rute-kort
                  if (track.trackType === 'planned' && track.plannedRoute) {
                    const pr = track.plannedRoute;
                    const statusLabel = track.planStatus === 'both'
                      ? t('routePlanner.plan_status_both', 'Planlagt + kjørt')
                      : track.planStatus === 'completed'
                      ? t('routePlanner.plan_status_completed', 'Gjennomført')
                      : t('routePlanner.plan_status_planned', 'Planlagt');
                    const statusColor = track.planStatus === 'both' ? 'bg-emerald-100 text-emerald-700'
                      : track.planStatus === 'completed' ? 'bg-blue-100 text-blue-700'
                      : 'bg-amber-100 text-amber-700';

                    return (
                      <div key={track.id} className="border border-stone-200 rounded-2xl bg-stone-50 overflow-hidden">
                        <div className="p-3">
                          {/* Header med status */}
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Navigation size={12} className="text-blue-500 flex-shrink-0 mt-0.5" />
                              <span className="text-[12px] font-bold text-stone-800 truncate">{track.name}</span>
                            </div>
                            <span className={`text-3xs font-bold px-2 py-0.5 rounded-full shrink-0 ${statusColor}`}>
                              {statusLabel}
                            </span>
                          </div>

                          {/* Fra → Til */}
                          <div className="flex items-center gap-1.5 text-2xs text-stone-500 mb-2">
                            <span className="truncate max-w-[40%]">{pr.fromName}</span>
                            <ArrowRight size={10} className="shrink-0" />
                            <span className="truncate max-w-[40%]">{pr.toName}</span>
                          </div>

                          {/* Stats */}
                          <div className="flex items-center gap-3 text-tiny text-[var(--text-muted)] mb-2">
                            <span className="flex items-center gap-1">
                              <Route size={10} className="text-blue-400" />
                              {formatDistance(track.distance)}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock size={10} className="text-stone-400" />
                              {formatDuration(pr.durationSeconds)}
                            </span>
                            <span className="flex items-center gap-1 text-emerald-600 font-bold">
                              <Tent size={10} /> {pr.fricampSpotCount}
                            </span>
                            <span className="flex items-center gap-1 text-violet-600 font-bold">
                              <Landmark size={10} /> {pr.attractionCount}
                            </span>
                            <span className="flex items-center gap-1 text-amber-600 font-bold">
                              <Star size={10} /> {pr.routeScore}
                            </span>
                          </div>

                          {/* Handlinger */}
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => { onViewTrack(track); onClose(); }}
                              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl bg-blue-500 text-white text-tiny font-bold hover:bg-blue-600 transition-all active:scale-95"
                            >
                              <MapPin size={11} />
                              {t("auto.mytracksmodal.vis_pa_kart", "Vis på kart")}
                            </button>
                            {track.planStatus === 'planned' && (
                              <button
                                onClick={async () => {
                                  await updateTrackPlanStatus(track.id, 'completed');
                                  setTracks(prev => prev.map(t => t.id === track.id ? { ...t, planStatus: 'completed' } : t));
                                }}
                                title={t('routePlanner.mark_completed', 'Marker som gjennomført')}
                                className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-emerald-100 text-[var(--text-muted)] hover:text-emerald-600 transition-all"
                              >
                                <Check size={14} />
                              </button>
                            )}
                            {confirmDeleteId === track.id ? (
                              <div className="flex gap-1">
                                <button onClick={() => setConfirmDeleteId(null)}
                                  className="px-2 py-1 rounded-lg bg-stone-100 text-[var(--text-muted)] text-2xs font-bold">
                                  {t('common.cancel')}
                                </button>
                                <button onClick={() => handleDelete(track)} disabled={!!deletingId}
                                  className="px-2 py-1 rounded-lg bg-red-500 text-white text-2xs font-bold hover:bg-red-600">
                                  {deletingId === track.id ? <Loader2 size={12} className="animate-spin" /> : t('common.delete')}
                                </button>
                              </div>
                            ) : (
                              <button onClick={() => setConfirmDeleteId(track.id)}
                                className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-red-100 text-[var(--text-secondary)] hover:text-red-500 transition-all">
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Standard registrert tur-kort (eksisterende)
                  return (
                <div key={track.id} className="border border-stone-200 rounded-2xl bg-stone-50 overflow-hidden">
                  <div className="p-3">
                    {/* Navn + dato */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <MapPin size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-[12px] font-bold text-stone-800 truncate">{track.name}</span>
                      </div>
                      <span className="text-2xs text-[var(--text-secondary)] flex-shrink-0">{formatDate(track.createdAt)}</span>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center gap-3 text-tiny text-[var(--text-muted)] mb-3">
                      <span className="flex items-center gap-1">
                        <Navigation size={10} className="text-emerald-400" />
                        {formatDistance(track.distance)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock size={10} className="text-blue-400" />
                        {formatDuration(track.duration)}
                      </span>
                      {track.elevationGain > 0 && (
                        <button
                          onClick={() => setExpandedProfileId(id => id === track.id ? null : track.id)}
                          className="flex items-center gap-1 hover:text-emerald-600 transition-colors"
                          title={t("auto.mytracksmodal.title.vis_hoydeprofil", "Vis høydeprofil")}
                        >
                          <TrendingUp size={10} className="text-amber-400" />
                          +{track.elevationGain}m
                          {expandedProfileId === track.id ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
                        </button>
                      )}
                    </div>

                    {/* Elevation chart */}
                    {expandedProfileId === track.id && (
                      <TrackElevationChart gpxUrl={track.gpxUrl} />
                    )}

                    {/* Handlingsknapper */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { onViewTrack(track); onClose(); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl bg-emerald-500 text-white text-tiny font-bold hover:bg-emerald-600 transition-all active:scale-95"
                      >
                        <MapPin size={11} />
                        {t("auto.mytracksmodal.vis_pa_kart", "Vis på kart")}
                      </button>
                      <button
                        onClick={() => setSharingTrack(track)}
                        title={t('tracks.share_track')}
                        className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-blue-100 text-[var(--text-muted)] hover:text-blue-600 transition-all"
                      >
                        <Share2 size={14} />
                      </button>
                      <button
                        onClick={() => exportTrackAsGpxDownload([], track.name)}
                        title={t("auto.mytracksmodal.title.last_ned_gpx", "Last ned GPX")}
                        className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-stone-200 text-[var(--text-muted)] transition-all"
                      >
                        <Download size={14} />
                      </button>
                      {track.linkedRouteId ? (
                        <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-emerald-100 text-emerald-500" title={t('tracks.route_linked')}>
                          <Check size={14} />
                        </div>
                      ) : (
                        <button
                          onClick={() => handleConvertToRoute(track)}
                          disabled={convertingId === track.id}
                          title={t('tracks.save_as_route')}
                          className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-emerald-100 text-[var(--text-muted)] hover:text-emerald-600 transition-all disabled:opacity-50"
                        >
                          {convertingId === track.id ? <Loader2 size={14} className="animate-spin" /> : <Route size={14} />}
                        </button>
                      )}
                      {confirmDeleteId === track.id ? (
                        <div className="flex gap-1">
                          <button
                            onClick={() => setConfirmDeleteId(null)}
                            className="px-2 py-1 rounded-lg bg-stone-100 text-[var(--text-muted)] text-2xs font-bold"
                          >
                            {t('common.cancel')}
                          </button>
                          <button
                            onClick={() => handleDelete(track)}
                            disabled={!!deletingId}
                            className="px-2 py-1 rounded-lg bg-red-500 text-white text-2xs font-bold hover:bg-red-600"
                          >
                            {deletingId === track.id
                              ? <Loader2 size={12} className="animate-spin" />
                              : t('common.delete')
                            }
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(track.id)}
                          className="w-8 h-8 flex items-center justify-center rounded-xl bg-stone-100 hover:bg-red-100 text-[var(--text-secondary)] hover:text-red-500 transition-all"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                  );
              })}
            </div>
          )}
        </div>
      </div>

      {sharingTrack && (
        <TrackShareDialog
          track={sharingTrack}
          user={user}
          onClose={() => setSharingTrack(null)}
          onShared={(routeId) => {
            if (routeId) {
              setTracks(prev => prev.map(t => t.id === sharingTrack.id ? { ...t, linkedRouteId: routeId } : t));
            }
            setSharingTrack(null);
          }}
        />
      )}
    </div>
  );
};

export default MyTracksModal;
